// https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/#explicit-rendering

import pDefer, { type DeferredPromise } from 'p-defer';

type GetToken = () => Promise<string | null>;
type FormatRequestOptions = (token: string | null) => Record<string, any>;

type UseInteractionOnlyTurnstileReturn = {
    turnstileContainerId: string;
    getToken: GetToken;
    formatRequestOptions: FormatRequestOptions;
};

type Nullable<T> = T | null;

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const scriptReadyDefer = pDefer();

export const useInteractionOnlyTurnstile = (action: string): UseInteractionOnlyTurnstileReturn => {
    const config = useRuntimeConfig();
    const { turnstileDisabled, turnstileSiteKey } = config.public;
    const { locale } = useTypedI18n();
    const { logError } = useLogs();

    let widgetId: Nullable<string> = null;
    let getTokenDefer: Nullable<DeferredPromise<string>> = null;

    // One container per instance: a widget cannot be shared, so the id is generated here and handed to the
    // <TurnstileChallenge :container-id> rendered by the caller. A global DOM id let nested instances (page + form,
    // page + auth modal) fight over a single node, and the loser was left without a widget.
    const containerId = useId();

    const { load } = useScript(SCRIPT_URL, {
        trigger: 'manual',
    });

    onBeforeMount(() => {
        if (turnstileDisabled) return;

        load().then(() => scriptReadyDefer.resolve());
    });

    // Registered before the caller's own onMounted (this composable is called at the top of its setup), so the
    // continuation below is queued on scriptReadyDefer ahead of any getToken() fired there and widgetId is set first
    // (BSD-0 / ba85ce221).
    onMounted(async () => {
        if (turnstileDisabled) return;

        // The challenge lives in the caller's own subtree, hence it is already in the DOM here. No element means no
        // <TurnstileChallenge /> for this instance: render nothing rather than a widget outside the layout —
        // intentional for instances whose getToken() is never reached, see login-exchange.vue in exchange mode.
        const container = document.getElementById(containerId);

        if (!container) return;

        await scriptReadyDefer.promise;

        widgetId =
            window.turnstile.render(container, {
                sitekey: turnstileSiteKey,
                theme: 'light',
                size: 'flexible',
                appearance: 'interaction-only',
                retry: 'auto',
                language: locale.value,
                action,
                callback: (token: string): void => {
                    getTokenDefer?.resolve(token);
                },
                'error-callback': (): void => {
                    getTokenDefer?.reject();
                },
                'expired-callback': (): void => {
                    getTokenDefer = null;
                },
            }) ?? null;
    });

    const removeWidget = (): void => {
        if (!widgetId) return;

        // The container can be unmounted before us (StoryblokSellForm hides the form on success): Turnstile then has
        // nothing left to remove.
        if (document.getElementById(containerId)) window.turnstile.remove(widgetId);

        widgetId = null;
    };

    onBeforeUnmount(() => {
        if (turnstileDisabled) return;

        removeWidget();
    });

    const getToken: GetToken = async () => {
        if (turnstileDisabled) return null;

        await scriptReadyDefer.promise;

        if (!widgetId) {
            logError('Turnstile getToken called with no rendered widget: is <TurnstileChallenge /> mounted?', { action });

            return null;
        }

        await getTokenDefer?.promise;

        const tokenResolved = !!getTokenDefer && (await isResolved(getTokenDefer.promise));
        const tokenRejected = !!getTokenDefer && (await isRejected(getTokenDefer.promise));

        if (!getTokenDefer || tokenRejected || tokenResolved) {
            getTokenDefer = pDefer<string>();

            // BSD-235 equivalent: token is single-use, reset before re-executing to avoid reuse errors.
            // Re-checked because onBeforeUnmount may have cleared widgetId while we awaited above.
            if (widgetId) window.turnstile.reset(widgetId);
        }

        return getTokenDefer.promise;
    };

    const formatRequestOptions: FormatRequestOptions = token => {
        if (turnstileDisabled) return {};

        if (!token) throw new Error('Error in turnstile formatRequestOptions: nullish token');

        return { headers: { 'cf-turnstile-response': token } };
    };

    return { turnstileContainerId: containerId, getToken, formatRequestOptions };
};
